import { useEffect, useState } from 'react';
import type { PassItem, Store } from '../api.js';
import { dayMonthYear } from '../day.js';
import { useViewing } from '../view-as.js';
import { onboarding, type ReadingBack } from './api.js';
import type { ReadingPlan } from './journey.js';
import './onboarding.css';

export const READING_BACK_EVERY_MS = 60_000;

export function foundReadingBack(item: Pick<PassItem, 'when' | 'features' | 'subjectKind'>, plan: ReadingPlan | null | undefined): boolean {
  // A deadline card cannot be judged until the triage read carries the source time.
  // Calendar, deal and contract clocks can also describe an event rather than a source.
  if (!plan || !item.when?.at || item.features.deadline?.at || ['calendar_event', 'deal', 'contract'].includes(item.subjectKind)) return false;
  const at = Date.parse(item.when.at);
  return at >= Date.parse(plan.historyStart) && at < Date.parse(plan.windowFrom);
}
export function ReadingBackStrip({ value }: { value: ReadingBack | null }) {
  if (!value?.active) return null;
  return <section className="reading-back"><b>Still reading back to {dayMonthYear(new Date(value.backTo))}</b>
    <p>{value.done}{value.expected !== null ? ` of ${value.expected}` : ''} older messages understood.</p></section>;
}
export function useReadingBack(stores: Store[]) {
  const viewing = useViewing();
  const viewKey = viewing ? `${viewing.sessionId}|${viewing.principalId}` : '';
  const key = stores.filter(s => s.kind === 'commercial').map(s => s.tenantId).join('|');
  const [values, setValues] = useState<Record<string, { reading: ReadingBack | null; plan: ReadingPlan | null }>>({});
  useEffect(() => {
    let live = true;
    let inFlight = false;
    let lastLoadedAt = Number.NEGATIVE_INFINITY;
    const storeIds = key.split('|').filter(Boolean);
    const finished = new Set<string>();
    const journeyLoaded = new Set<string>();
    let timer: number | undefined;
    setValues({});
    const schedule = () => {
      clearTimeout(timer);
      if (!live || inFlight || document.visibilityState === 'hidden' || !storeIds.some(store => !finished.has(store))) return;
      // A keeper view reads once, even if reading back remains active.
      if (viewKey && lastLoadedAt !== Number.NEGATIVE_INFINITY) return;
      const remaining = READING_BACK_EVERY_MS - (Date.now() - lastLoadedAt);
      if (remaining <= 0) void load();
      else timer = window.setTimeout(() => void load(), remaining);
    };
    const load = async () => {
      if (!live || inFlight || document.visibilityState === 'hidden') return;
      inFlight = true;
      await Promise.all(storeIds.filter(store => !finished.has(store)).map(async store => {
        const [reading, j] = await Promise.allSettled([
          onboarding.readingBack(store),
          journeyLoaded.has(store) ? Promise.resolve(null) : onboarding.journey(store),
        ]);
        if (reading.status === 'fulfilled' && !reading.value?.active) finished.add(store);
        if (j.status === 'fulfilled' && j.value) journeyLoaded.add(store);
        if (live) setValues(v => ({ ...v, [store]: {
          reading: reading.status === 'fulfilled' ? reading.value : null,
          plan: j.status === 'fulfilled' && j.value ? j.value.journey.plan : v[store]?.plan ?? null,
        } }));
      }));
      inFlight = false;
      lastLoadedAt = Date.now();
      schedule();
    };
    document.addEventListener('visibilitychange', schedule);
    schedule();
    return () => { live = false; clearTimeout(timer); document.removeEventListener('visibilitychange', schedule); };
  }, [key, viewKey]);
  return values;
}
